// src/context/CartContext.jsx
import React, { createContext, useState, useContext, useEffect } from 'react';
import { useUser } from './UserContext';
import { cartService } from '../services/cartService';
import { orderService } from '../services/orderService';

const CartContext = createContext();

const getValidId = (item) => {
  if (!item) return '';
  const id = item.outletVariantId || item.variantId || item.id;
  return id ? String(id) : '';
};

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([]);
  const [cartData, setCartData] = useState(null);
  const [deliveryConfig, setDeliveryConfig] = useState({ freeDeliveryAbove: 0, deliveryFee: 0, codCharge: 0 });

  const { isLoggedIn, user } = useUser();

  // Fetch Delivery Config on Mount
  useEffect(() => {
    const fetchDeliveryInfo = async () => {
      const config = await orderService.getDeliveryConfig();
      if (config) setDeliveryConfig(config);
    };
    fetchDeliveryInfo();
  }, []);

  const updateCartState = (res) => {
    setCartItems(res?.items || []);
    setCartData(res);
  };

  const updateLocalState = (items) => {
    setCartItems(items);
    const total = items.reduce((acc, item) => acc + ((item.sellingPrice || item.price || item.mrp || 0) * (item.quantity || 1)), 0);
    setCartData({ 
      totalAmt: total, 
      discountAmt: 0, 
      payableAmt: total, 
      deliveryFee: 0,
      totalTax: 0 
    });
  };

  // Sync Logic: Local -> Backend
  useEffect(() => {
    const syncAndFetchCart = async () => {
      if (isLoggedIn() && user) {
        try {
          const localCart = JSON.parse(localStorage.getItem('svmart_cart_local') || '[]');
          
          if (localCart.length > 0) {
            for (const item of localCart) {
              const vId = getValidId(item);
              if (vId) {
                 let currentQty = item.quantity || 1;
                 try {
                   const res = await cartService.addToCart(vId);
                   const addedItem = res.items.find(i => getValidId(i) === vId);
                   if (addedItem && currentQty > 1) {
                       for(let q = 1; q < currentQty; q++){
                           await cartService.increaseQuantity(addedItem.cartItemId);
                       }
                   }
                 } catch(err) {
                   console.error("Sync error for item", vId, err);
                 }
              }
            }
            localStorage.removeItem('svmart_cart_local');
          }
          
          const data = await cartService.getCart();
          updateCartState(data);
        } catch (error) {
          console.error("Error syncing cart:", error);
        }
      } else {
        const localCart = JSON.parse(localStorage.getItem('svmart_cart_local') || '[]');
        updateLocalState(localCart);
      }
    };

    syncAndFetchCart();
  }, [isLoggedIn, user]);

  useEffect(() => {
    if (!isLoggedIn()) {
      localStorage.setItem('svmart_cart_local', JSON.stringify(cartItems));
    }
  }, [cartItems, isLoggedIn]);

  const getCartItem = (product) => {
    const vId = getValidId(product);
    return cartItems.find(item => getValidId(item) === vId);
  };

  const addToCart = async (product) => {
    const vId = getValidId(product);
    if (isLoggedIn()) {
      try {
        const res = await cartService.addToCart(vId);
        updateCartState(res);
      } catch (e) { 
        console.error("Error adding to cart", e);
        alert(e.response?.data?.message || "Cannot add item to cart.");
      }
    } else {
      const prev = [...cartItems];
      const existing = prev.find(item => getValidId(item) === vId);
      const stockLimit = product.stock !== undefined ? product.stock : 1;
      
      if (existing) {
         if (existing.quantity >= stockLimit) {
             alert(`Only ${stockLimit} items are available in stock.`);
             return;
         }
         const updated = prev.map(item => getValidId(item) === vId ? { ...item, quantity: (item.quantity || 1) + 1 } : item);
         updateLocalState(updated);
      } else {
         if (stockLimit <= 0) {
             alert(`Out of stock.`);
             return;
         }
         updateLocalState([...prev, { ...product, outletVariantId: vId, quantity: 1 }]);
      }
    }
  };

  const increaseQuantity = async (item) => {
    if (isLoggedIn()) {
      try {
        if (item.cartItemId) {
          const res = await cartService.increaseQuantity(item.cartItemId);
          updateCartState(res);
        }
      } catch (e) { 
        console.error("Error increasing qty", e);
        alert(e.response?.data?.message || "Cannot increase quantity.");
      }
    } else {
      const vId = getValidId(item);
      const stockLimit = item.stock !== undefined ? item.stock : 1;
      
      if (item.quantity >= stockLimit) {
         alert(`Only ${stockLimit} items are available in stock.`);
         return;
      }
      const updated = cartItems.map(p => getValidId(p) === vId ? { ...p, quantity: (p.quantity || 1) + 1 } : p);
      updateLocalState(updated);
    }
  };

  const decreaseQuantity = async (item) => {
    if (isLoggedIn()) {
      try {
        if (item.cartItemId) {
          if (item.quantity <= 1) {
            const res = await cartService.removeFromCart(item.cartItemId);
            updateCartState(res);
          } else {
            const res = await cartService.decreaseQuantity(item.cartItemId);
            updateCartState(res);
          }
        }
      } catch (e) { console.error("Error decreasing qty", e); }
    } else {
      const vId = getValidId(item);
      let updated;
      if (item.quantity <= 1) {
        updated = cartItems.filter(p => getValidId(p) !== vId);
      } else {
        updated = cartItems.map(p => getValidId(p) === vId ? { ...p, quantity: p.quantity - 1 } : p);
      }
      updateLocalState(updated);
    }
  };

  const removeFromCart = async (item) => {
    if (isLoggedIn()) {
      try {
        if (item.cartItemId) {
          const res = await cartService.removeFromCart(item.cartItemId);
          updateCartState(res);
        }
      } catch (e) { console.error("Error removing from cart", e); }
    } else {
      const vId = getValidId(item);
      const updated = cartItems.filter(p => getValidId(p) !== vId);
      updateLocalState(updated);
    }
  };

  // Changed: isOrderSuccess parameter added to instantly clear local state after order
  const clearCart = (isOrderSuccess = false) => {
     if (isOrderSuccess) {
        setCartItems([]);
        setCartData({ totalAmt: 0, discountAmt: 0, payableAmt: 0, deliveryFee: 0, totalTax: 0 });
        localStorage.removeItem('svmart_cart_local');
     } else {
        if (!isLoggedIn()) {
           updateLocalState([]);
        } else {
           cartItems.forEach(item => removeFromCart(item));
        }
     }
  };

  // COUPON LOGIC
  const applyCouponCode = async (code) => {
    if (isLoggedIn()) {
      try {
        const res = await orderService.applyCoupon(code);
        updateCartState(res);
        return { success: true };
      } catch (error) {
        console.error("Coupon error", error);
        return { success: false, message: error.response?.data?.message || 'Invalid or Expired Coupon' };
      }
    }
    return { success: false, message: 'Please login to apply coupons' };
  };

  const removeCouponCode = async () => {
    if (isLoggedIn()) {
      try {
        const res = await orderService.removeCoupon();
        updateCartState(res);
      } catch (error) {
        console.error("Remove coupon error", error);
      }
    }
  };

  const getCartCount = () => {
    return cartItems.length;
  };

  const getCartTotal = () => {
    return cartItems.reduce((total, item) => total + ((item.sellingPrice || item.price || item.mrp || 0) * (item.quantity || 1)), 0);
  };

  const value = {
    cartItems,
    cartData,
    deliveryConfig, 
    addToCart,
    removeFromCart,
    increaseQuantity,
    decreaseQuantity,
    getCartItem,
    getCartCount,
    getCartTotal, 
    clearCart,
    applyCouponCode,
    removeCouponCode
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
};